import * as XLSX from 'xlsx';
import { ProjectServices } from '../../../../Services/ProjectServices';
import { formatApiErrorMessage } from '../../../../utils/userProfileFields';
import {
    buildAssignmentMap,
    makeLicenceKey,
    normalizeLicence,
    normalizeUser,
    parseLicenceKey,
} from './assignmentModel';
import {
    buildTemplateCsv,
    buildTemplateWorkbook,
    buildUploadPreview,
    getTemplateFilename,
    readWorkbookRows,
} from './assignmentFile';

const projectServices = new ProjectServices();

/**
 * REACT_APP_ASSIGNMENT_API: "auto" (default) tries the assignment endpoints and
 * falls back to the legacy licence endpoints when they are not deployed;
 * "v2" uses only the new endpoints; "legacy" never calls them.
 */
const API_MODE = (process.env.REACT_APP_ASSIGNMENT_API || 'auto').toLowerCase();
const initialSupport = API_MODE === 'v2' ? true : API_MODE === 'legacy' ? false : null;
const v2Support = {
    matrix: initialSupport,
    batch: initialSupport,
    template: initialSupport,
    upload: initialSupport,
};

const isMissingEndpoint = error => [404, 405, 501].includes(error?.response?.status);

const withFallback = async (feature, v2Call, legacyCall) => {
    if (v2Support[feature] !== false) {
        try {
            const result = await v2Call();
            v2Support[feature] = true;
            return result;
        } catch (error) {
            if (v2Support[feature] === null && isMissingEndpoint(error)) {
                v2Support[feature] = false;
            } else if (error?.response?.data && !(error.response.data instanceof Blob)) {
                return error.response.data;
            } else {
                throw error;
            }
        }
    }
    return legacyCall();
};

const LEGACY_GROUPS = [
    { type: 'assessment', listKey: 'assessments', idKey: 'assessment_id', nameKey: 'assessment_name' },
    { type: 'course', listKey: 'courses', idKey: 'course_id', nameKey: 'course_name' },
    { type: 'apprenticeship', listKey: 'apprenticeships', idKey: 'apprenticeship_id', nameKey: 'apprenticeship_name' },
];

const legacyGroupByType = LEGACY_GROUPS.reduce((items, group) => {
    items[group.type] = group;
    return items;
}, {});

export const buildLegacyMatrix = (userData, licenceData) => {
    const rawUsers = Array.isArray(userData?.results) ? userData.results : (Array.isArray(userData) ? userData : []);
    const users = rawUsers
        .filter(user => user?.role === 'user' && user.uuid)
        .map(user => normalizeUser({
            uuid: user.uuid,
            email: user.email || user.userName || '',
            first_name: user.firstName || user.first_name || '',
            last_name: user.lastName || user.last_name || '',
            role: user.role,
            legacy: {
                role: user.role,
                uuid: user.uuid,
                customer_users: user.userName || user.email || '-',
            },
        }));

    const licences = [];
    const assignments = [];
    LEGACY_GROUPS.forEach(({ type, listKey, idKey, nameKey }) => {
        (licenceData?.[listKey] || []).forEach((item) => {
            const id = String(item[idKey]);
            const userIds = (item.users || []).map(user => user?.uuid).filter(Boolean).map(String);
            const assigned = Number.isFinite(Number(item.user_count)) ? Number(item.user_count) : userIds.length;
            const allocated = Number(item.left_count) || 0;
            licences.push(normalizeLicence({
                type,
                id,
                cl_uuid: item.cl_uuid,
                name: item[nameKey],
                provider: item.provider,
                allocated,
                assigned,
                remaining: allocated - assigned,
            }));
            userIds.forEach(userId => assignments.push({ user_uuid: userId, licence_key: makeLicenceKey(type, id) }));
        });
    });

    return { users, licences, assignments };
};

const normalizeMatrix = data => ({
    users: (data?.users || []).filter(user => user?.uuid).map(normalizeUser),
    licences: (data?.licences || []).map(normalizeLicence),
    assignments: (data?.assignments || []).map(item => ({
        user_uuid: String(item.user_uuid),
        licence_key: String(item.licence_key),
    })),
});

const toMatrixState = (matrix) => ({
    ...matrix,
    baseline: buildAssignmentMap(matrix.licences, matrix.assignments),
});

const MATRIX_PAGE_LIMIT = 40;

/** When the matrix API pages its users, read every page so the matrix holds all learners. */
const readAllMatrixPages = async (projectId, first) => {
    const pagination = first?.pagination;
    const total = Number(pagination?.total);
    const firstUsers = first?.users || [];
    if (!pagination || !Number.isFinite(total) || total <= firstUsers.length) return first;
    const pageSize = Number(pagination.page_size) || firstUsers.length || 100;
    const pages = Math.min(Math.ceil(total / pageSize), MATRIX_PAGE_LIMIT);
    const merged = { ...first, users: [...firstUsers], assignments: [...(first.assignments || [])] };
    for (let page = (Number(pagination.page) || 1) + 1; page <= pages; page += 1) {
        // eslint-disable-next-line no-await-in-loop
        const response = await projectServices.getAssignmentMatrix(projectId, { page, pageSize });
        const data = response?.data || response;
        merged.users.push(...(data?.users || []));
        merged.assignments.push(...(data?.assignments || []));
    }
    return merged;
};

/** User ids matching attribute filters, answered by the server. */
export const fetchMatchingUserIds = async (projectId, filterParams) => {
    const response = await projectServices.getAssignmentMatrix(projectId, { filters: filterParams });
    const data = await readAllMatrixPages(projectId, response?.data || response);
    return (data?.users || []).map(user => String(user.uuid));
};

export const loadAssignmentMatrix = async (projectId) => {
    const result = await withFallback(
        'matrix',
        async () => {
            const response = await projectServices.getAssignmentMatrix(projectId);
            if (response?.status === false) throw new Error(formatApiErrorMessage(response, 'Assignments could not be loaded.'));
            return normalizeMatrix(await readAllMatrixPages(projectId, response?.data || response));
        },
        async () => {
            const [userRes, licenceRes] = await Promise.all([
                projectServices.userByProject(projectId),
                projectServices.getDetailLicenseCustomer(projectId),
            ]);
            return buildLegacyMatrix(userRes?.data, licenceRes?.status ? licenceRes.data : null);
        },
    );
    // withFallback hands back the error body of a failed request; it is not matrix data.
    if (!Array.isArray(result?.users) || !Array.isArray(result?.licences)) {
        throw new Error(formatApiErrorMessage(result, 'Assignments could not be loaded.'));
    }
    return toMatrixState(result);
};

const isLegacyFailure = result => (
    !result
    || result instanceof Error
    || result.status === false
    || (typeof result.message === 'string' && result.message.includes('Cannot Assign the licence remaining counts'))
);

const groupLegacyEntries = (entries, userById) => {
    const payload = {};
    entries.forEach(({ licence_key: key, user_uuids: userIds }) => {
        const { type, id } = parseLicenceKey(key);
        const group = legacyGroupByType[type];
        if (!group) return;
        if (!payload[group.listKey]) payload[group.listKey] = [];
        payload[group.listKey].push({
            [group.idKey]: id,
            user: userIds.map(userId => userById[userId] || { uuid: userId, role: 'user', customer_users: '-' }),
        });
    });
    return payload;
};

const buildLegacyLabEntries = (entries, labs, userById) => entries.flatMap(({ licence_key: key, user_uuids: userIds }) => {
    const { type, id } = parseLicenceKey(key);
    if (type !== 'course') return [];
    const users = userIds.map(userId => userById[userId] || { uuid: userId, role: 'user', customer_users: '-' });
    return labs
        .filter(lab => String(lab.course_id) === id)
        .map(lab => ({ lab_id: lab.uuid, user: users }));
});

const saveLegacyBatch = async (projectId, diff, users) => {
    const userById = users.reduce((items, user) => {
        items[user.uuid] = user.legacy || { uuid: user.uuid, role: user.role || 'user', customer_users: user.email || '-' };
        return items;
    }, {});
    const errors = [];
    const courseIds = [...new Set([...diff.add, ...diff.remove]
        .map(entry => parseLicenceKey(entry.licence_key))
        .filter(({ type }) => type === 'course')
        .map(({ id }) => id))];

    let labs = [];
    if (courseIds.length) {
        const labRes = await projectServices.getLabsByCourse({ course_ids: courseIds });
        labs = Array.isArray(labRes?.data) ? labRes.data : [];
    }

    const run = async (call, payload, fallback) => {
        if (!Object.keys(payload).length) return;
        try {
            const result = await call(payload, projectId);
            if (isLegacyFailure(result)) errors.push(formatApiErrorMessage(result, fallback));
        } catch (error) {
            errors.push(formatApiErrorMessage(error?.response?.data || error?.message, fallback));
        }
    };

    const removeLabs = buildLegacyLabEntries(diff.remove, labs, userById);
    const addLabs = buildLegacyLabEntries(diff.add, labs, userById);
    const removeAllocate = (payload, id) => projectServices.removeAllocateUser(payload, id);
    const addAllocate = (payload, id) => projectServices.addAllocateUser(payload, id);

    for (const [listKey, items] of Object.entries(groupLegacyEntries(diff.remove, userById))) {
        await run(removeAllocate, { [listKey]: items }, 'Some licences could not be removed.');
    }
    if (removeLabs.length) await run(removeAllocate, { labs: removeLabs }, 'Some labs could not be removed.');
    for (const [listKey, items] of Object.entries(groupLegacyEntries(diff.add, userById))) {
        await run(addAllocate, { [listKey]: items }, 'Some licences could not be assigned.');
    }
    if (addLabs.length) await run(addAllocate, { labs: addLabs }, 'Some labs could not be assigned.');

    return errors.length
        ? { status: false, message: errors.join(' ') }
        : { status: true, message: 'Assignments saved.', data: { added: diff.addedCount, removed: diff.removedCount } };
};

export const saveAssignmentDiff = (projectId, diff, users) => withFallback(
    'batch',
    () => projectServices.saveAssignmentsBatch(projectId, { add: diff.add, remove: diff.remove, dry_run: false }),
    () => saveLegacyBatch(projectId, diff, users),
);

export const formatBatchErrors = (result) => {
    const errors = Array.isArray(result?.data?.errors) ? result.data.errors : [];
    const details = errors.map(error => (
        `${error.name || error.licence_key}: requested ${error.requested}, ${error.remaining} left`
    )).join('; ');
    return [formatApiErrorMessage(result, 'Assignments could not be saved.'), details].filter(Boolean).join(' ');
};

const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 30000);
};

export const downloadAssignmentTemplate = ({
    projectId,
    projectName,
    users,
    licences,
    baseline,
    fileFormat = 'xlsx',
}) => withFallback(
    'template',
    async () => {
        const { blob, filename } = await projectServices.downloadAssignmentTemplate(projectId, fileFormat);
        downloadBlob(blob, filename || getTemplateFilename(projectName, fileFormat));
    },
    async () => {
        const templateArgs = { projectId, users, licences, assignments: baseline };
        if (fileFormat === 'csv') {
            const csv = buildTemplateCsv(templateArgs);
            downloadBlob(new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8' }), getTemplateFilename(projectName, 'csv'));
            return;
        }
        XLSX.writeFile(buildTemplateWorkbook(templateArgs), getTemplateFilename(projectName));
    },
);

export const previewAssignmentUpload = ({ projectId, file, users, licences, baseline }) => withFallback(
    'upload',
    async () => {
        const result = await projectServices.previewAssignmentUpload(projectId, file);
        if (!result || result.status === false) {
            return { error: formatApiErrorMessage(result, 'The file could not be read.') };
        }
        const data = result.data || {};
        return {
            source: 'server',
            uploadId: data.upload_id,
            summary: data.summary || {},
            per_licence: data.per_licence || [],
            issues: data.issues || [],
        };
    },
    async () => {
        const buffer = await file.arrayBuffer();
        const { rows, meta } = readWorkbookRows(new Uint8Array(buffer));
        return { source: 'client', ...buildUploadPreview({ rows, meta, users, licences, baseline }) };
    },
);

export const applyAssignmentUpload = ({ projectId, preview, users }) => {
    if (preview.source === 'server') {
        return projectServices.applyAssignmentUpload(projectId, preview.uploadId)
            .catch(error => error?.response?.data || { status: false, message: error?.message });
    }
    return saveAssignmentDiff(projectId, preview.diff, users);
};
